import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApartmentOutlined } from '@ant-design/icons';
import { Empty, Select, Switch, Tooltip } from 'antd';
import {
  CREATING,
  UnderlyingAsset,
  selectors,
  togglePerSectorAlpha,
  updateSessionSectorBenchmark,
} from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import Field from '../../SessionSettingsShared/components/Field';

const ETF_RESOURCE_TYPES = [
  'ETF',
  'ETFEquities',
  'ETFBonds',
  'ETFCommodities',
] as const;

interface Props {
  /** 'column' = the composition view's right column (two stacked fill-cards, no
   *  divider); 'bento' = inline fragment with a labelled divider + sized tiles
   *  (used in the constant / individual layouts). */
  variant?: 'column' | 'bento';
}

/**
 * Alpha monitoring (historical only). Same redux wiring as the classic
 * SectorAlphaPanel + PerSectorAlphaSettings (togglePerSectorAlpha,
 * updateSessionSectorBenchmark). Renders in two shapes — see `variant`.
 */
const AlphaMonitoringV2: React.FC<Props> = ({ variant = 'bento' }) => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const underlyingAssets = useSelector(selectors.underlyingAssets);

  const locked = session.status !== CREATING;
  const on = session.per_sector_alpha;
  const settings = session.sector_alpha_settings || {
    playable_sectors: [],
    session_sectors: [],
  };

  const benchmarkBySector = useMemo(
    () =>
      settings.session_sectors.reduce<Record<string, number | null>>(
        (acc, row) => {
          acc[row.sector_name] = row.benchmark_asset_id;
          return acc;
        },
        {},
      ),
    [settings.session_sectors],
  );

  const etfOptions = useMemo(() => {
    const options = (session.underlying_assets ?? []).reduce<UnderlyingAsset[]>(
      (acc, assetId) => {
        const asset = underlyingAssets[assetId];
        if (
          asset &&
          ETF_RESOURCE_TYPES.includes(
            asset.resourcetype as (typeof ETF_RESOURCE_TYPES)[number],
          )
        ) {
          acc.push(asset);
        }
        return acc;
      },
      [],
    );
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [session.underlying_assets, underlyingAssets]);

  const sectors = settings.playable_sectors;
  const assignedCount = sectors.filter(
    s => benchmarkBySector[s] != null,
  ).length;

  const perSectorFields = (
    <>
      <Field
        label="Per-sector alpha monitoring"
        hint="Track alpha attribution per sector against benchmark ETFs."
      >
        <Tooltip
          title={locked ? 'Cannot modify after the session has started' : ''}
        >
          <span>
            <Switch
              checked={on}
              disabled={locked}
              onChange={value =>
                dispatch(
                  togglePerSectorAlpha({
                    session_id: session.id,
                    per_sector_alpha: value,
                  }),
                )
              }
            />
          </span>
        </Tooltip>
      </Field>
      <Field label="Coverage">
        <span className="bmv2-pill-stat">
          <span className={`bmv2-dot bmv2-dot--${on ? 'on' : 'off'}`} />
          {on
            ? `${assignedCount}/${sectors.length} sectors assigned`
            : 'Monitoring off'}
        </span>
      </Field>
    </>
  );

  const sectorsExtra = (
    <span className="bmv2-pill-stat">
      {sectors.length} playable sector{sectors.length === 1 ? '' : 's'}
    </span>
  );

  const sectorsContent = !on ? (
    <div className="bmv2-empty bmv2-empty--tile">
      <span className="bmv2-empty__icon">⊘</span>
      <span className="bmv2-empty__title">Monitoring is off</span>
      <span className="bmv2-empty__sub">
        Enable per-sector alpha monitoring to assign a benchmark ETF to each
        playable sector.
      </span>
    </div>
  ) : sectors.length === 0 ? (
    <div className="bmv2-empty bmv2-empty--tile">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No playable sectors yet. Select and save session instruments first."
      />
    </div>
  ) : (
    <div className="bmv2-blist">
      {sectors.map(sectorName => (
        <div className="bmv2-srow" key={sectorName}>
          <span className="bmv2-srow-name" title={sectorName}>
            {sectorName}
          </span>
          <div className="bmv2-srow-select">
            <Select
              style={{ width: '100%' }}
              size="small"
              showSearch
              allowClear
              placeholder="Select benchmark ETF"
              disabled={locked}
              value={benchmarkBySector[sectorName] ?? undefined}
              optionFilterProp="children"
              notFoundContent="No ETF instruments selected for this session"
              onChange={value =>
                dispatch(
                  updateSessionSectorBenchmark({
                    session_id: session.id,
                    sector_name: sectorName,
                    benchmark_asset_id: value ?? null,
                  }),
                )
              }
            >
              {etfOptions.map(asset => (
                <Select.Option key={asset.id} value={asset.id}>
                  {asset.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      ))}
    </div>
  );

  // Right column of the composition grid: two stacked fill-cards, no divider
  // (the column itself is the separation cue).
  if (variant === 'column') {
    return (
      <>
        <div className="bmv2-ralpha">
          <SettingsCard title="Per-sector alpha" className="bmv2-card">
            {perSectorFields}
          </SettingsCard>
        </div>
        <div className="bmv2-rsectors">
          <SettingsCard
            title="Sector benchmarks"
            className="bmv2-card bmv2-flexbody"
            extra={sectorsExtra}
          >
            {sectorsContent}
          </SettingsCard>
        </div>
      </>
    );
  }

  // Bento fragment with a labelled divider (constant / individual layouts).
  return (
    <>
      <div className="bmv2-group-sep">
        <span className="bmv2-group-sep__label">
          <ApartmentOutlined />
          Alpha monitoring
        </span>
        <span className="bmv2-group-sep__line" />
      </div>

      <SettingsCard
        title="Per-sector alpha"
        className="bmv2-tile bmv2-tile--alpha"
      >
        {perSectorFields}
      </SettingsCard>

      <SettingsCard
        title="Sector benchmarks"
        className="bmv2-tile bmv2-tile--sectors bmv2-flexbody"
        extra={sectorsExtra}
      >
        {sectorsContent}
      </SettingsCard>
    </>
  );
};

export default AlphaMonitoringV2;
